import { toRaw } from "vue";
import { defineStore } from "pinia";
import isEmpty from "lodash/isEmpty";
import mapValues from "lodash/mapValues";
import omitBy from "lodash/omitBy";
import { NUMERIC_RE } from "@/directives/number";
import db from "@/db";
import { createChangeTracker } from "@/utils/changeTracker";
import { installationTemplateRestClient } from "@/api";
import { buildQueryset } from "@/utils/queryset";
import crudGenerator from "@/api/utils/offlineSync";
import { useProjectsStore } from "@/store/modules/projects";
import { useVlesStore } from "@/store/modules/vles";
import { useConfigurationLinesStore } from "@/store/modules/configurationLines";

const { innerFetch, innerCreate, innerDelete, innerPartialUpdate } =
  crudGenerator(db.models.Installation);

const NUMBER_KEYS = new Set([
  "axesNumber",
  "calculatedFlow",
  "chimneyHeight",
  "clearanceZone",
  "co2ExpectedContent",
  "explorableAxes",
  "h2oExpectedContent",
  "o2ExpectedContent",
  "outletHeight",
  "outletInternalDiameter",
  "outletWidth",
  "platformDistanceFromGround",
  "platformDistanceFromSamplingPort",
  "referenceLevel",
  "samplingPointHeight",
  "samplingPointInternalDiameter",
  "samplingPointWidth",
]);

const isPartialNumber = (value, key) =>
  NUMBER_KEYS.has(key) && typeof value === "string" && !NUMERIC_RE.test(value);

const toSendableForm = (form) =>
  omitBy(
    mapValues(toRaw(form), (value, key) =>
      NUMBER_KEYS.has(key) && value === "" ? null : value
    ),
    isPartialNumber
  );

const changeTrackers = new Map();

const changeTrackerOf = (uuid) => {
  if (!changeTrackers.has(uuid))
    changeTrackers.set(uuid, createChangeTracker());
  return changeTrackers.get(uuid);
};

export const useInstallationsStore = defineStore("installations", {
  state: () => ({
    installationList: [],
    totalInstallations: 0,
    installationIsLoading: false,
    currentInstallation: null,
    lastUpdated: new Date(),
  }),
  getters: {},
  actions: {
    async refreshLastUpdated() {
      this.lastUpdated = new Date();
    },

    async setCurrentInstallation(installationUuid) {
      let installation = null;
      if (installationUuid) {
        installation = await db.installations.get({ uuid: installationUuid });
      }
      this.currentInstallation = installation;
      return installation;
    },

    async updateInstallation({ form, fields, setAsCurrent }) {
      const tracker = changeTrackerOf(form.uuid);
      let payload = toSendableForm(form);
      if (fields) {
        const stored = await db.installations.get({ uuid: form.uuid });
        const changes = tracker.changes(payload, { fields, base: stored });
        if (isEmpty(changes)) return;
        payload = { uuid: form.uuid, ...changes };
      }
      await innerPartialUpdate(payload, useProjectsStore().isOfflineState);
      const installation = await db.installations.get({ uuid: form.uuid });
      if (fields) tracker.commit(payload, installation);

      const index = this.installationList.findIndex(
        ({ uuid }) => uuid === installation.uuid
      );
      if (index >= 0) {
        this.installationList[index] = installation;
        this.installationList = [...this.installationList];
      }

      if (setAsCurrent) this.currentInstallation = installation;
    },

    async fetchInstallations(projectUuid) {
      return innerFetch(
        { project_uuid: projectUuid },
        useProjectsStore().isOfflineState,
        (isLoading) => {
          this.installationIsLoading = isLoading;
        }
      );
    },

    async filterInstallations({ queryAsObject, ordering, filterMapping }) {
      while (this.installationIsLoading)
        await new Promise((r) => setTimeout(r, 200));
      this.installationIsLoading = true;
      try {
        const { queryset, count } = await buildQueryset(
          db.installations,
          queryAsObject,
          ordering,
          filterMapping
        );
        this.installationList = await queryset.toArray();
        this.totalInstallations = count;
      } finally {
        this.installationIsLoading = false;
      }
    },

    async createInstallation(form) {
      form.updatedAt = new Date();
      const installation = await innerCreate(
        {
          ...form,
          operatingHoursPerDay: 24,
          operatingDaysPerYear: 365,
        },
        useProjectsStore().isOfflineState,
        { requiresNetwork: true }
      );
      this.installationList.push(installation);
      this.totalInstallations += 1;
      try {
        const filters = { installationUuid: installation.uuid };
        await useVlesStore().fetchVles(installation.project, filters);
        await useConfigurationLinesStore().fetchConfigurationLines(
          installation.project,
          filters
        );
      } catch (e) {
        console.error(e);
      }

      return installation;
    },

    async createInstallationFromTemplate(form) {
      const response =
        await installationTemplateRestClient.createInstallationFromTemplate(
          useProjectsStore().currentProjectUuid,
          form
        );
      const uuid = await db.installations.add(response);
      const installation = await db.installations.get({ uuid });
      this.installationList.push(installation);
      this.totalInstallations += 1;
      return installation;
    },

    async createTemplateFromInstallation(installation) {
      await installationTemplateRestClient.createInstallationTemplate(
        installation
      );
    },

    async setProjectInstallations(projectId) {
      while (this.installationIsLoading)
        await new Promise((r) => setTimeout(r, 200));
      this.installationIsLoading = true;
      try {
        const installationList = await db.installations
          .where("project")
          .equals(projectId)
          .toArray();
        this.installationList = installationList;
        this.totalInstallations = installationList.length;
      } finally {
        this.installationIsLoading = false;
      }
    },

    async deleteInstallation(uuid) {
      const projectsStore = useProjectsStore();
      await innerDelete(
        uuid,
        projectsStore.isOfflineState,
        projectsStore.currentProjectUuid
      );
      this.installationList = this.installationList.filter(
        (installation) => installation.uuid !== uuid
      );
      this.totalInstallations -= 1;
    },
  },
});
